import React, {FC, useEffect, useState} from 'react';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {useParams} from "react-router-dom";
import {tokenService} from '../../../store/actions';
import {useTranslation} from "react-i18next";
import {
    formatNumber,
    formatNumberWithCommas,
    formatPercent,
    formatUnit,
    getFiat,
    serializeQuery,
    toFormatNumber,
    toXDCFormatNumber,
    toXDCNumber,
    toXDCNumberWithoutFormat
} from "../../../common/utils/function";
import useCommon from "../../../common/hooks/useCommon";
import {useSelector} from "react-redux";
import {Dropdown} from 'react-bootstrap';
import TokenPriceChart from './common/tokens-charts';

interface AnalysisList {
    name: string;
    type: string;
    fiat_value: string;
    exchange_rate: string;
    total_supply: string;
    symbol: string;
    holders: string;
    transactionCount: string;
    address: {
        hash: string;
        name: string | null;
        transactions_count: number;
    };
    decimals: number;
    circulating_market_cap: number;
    circulating_market_cap_usd: number;
    categories: any[];
    stats: any[];
    token: any;
    value: any;
    newPercentage?: string;
    newValue?: string;
    coingecko_data?: any;
}

const AnalysisTab: FC = () => {
    const {address, token, holder} = useParams();
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [transactionList, setTransactionList] = useState<AnalysisList[]>([]);
    const [newTransactionList, setNewTransactionList] = useState<AnalysisList[]>([]);
    const [pagination, setPagination] = useState<any>({limit: 50, page: 1});
    const [totalRecords, setTotalRecords] = useState<any>(0);
    const fiat = useSelector((e: Object) => (e as any)?.common?.fiat) || 'usd';
    const {get0x} = useCommon();
    const [transferCount, setTransferCount] = useState({
        token_holders_count: "",
        transfers_count: "",
    });
    const [stats, setStats] = useState<AnalysisList>({
        name: "",
        type: "",
        fiat_value: "0",
        exchange_rate: "0",
        total_supply: "0",
        symbol: "",
        holders: "",
        transactionCount: "",
        address: {
            hash: "",
            name: "",
            transactions_count: 0,
        },
        decimals: 0,
        circulating_market_cap: 0,
        circulating_market_cap_usd: 0,
        categories: [],
        stats: [],
        token: [],
        value: "",
    });

    const [selectedOption, setSelectedOption] = useState<string>('Top 50');

    const limitMap: { [key: string]: number } = {
        'Top 10': 10,
        'Top 20': 20,
        'Top 50': 50,
        'Top 100': 100
    };

    const handleSelect = (option: string) => {
        setSelectedOption(option);

        setPagination({
            limit: limitMap[option],
            page: 1
        });
    };

    const [options, setOptions] = useState({
        chart: {type: 'pie'},
        title: {text: ''},
        exporting: {enabled: false},
        credits: {enabled: false},
        subtitle: {text: ''},
        series: [{name: 'Holdings', data: [{name: 'Loading...', y: 100}]}],
        tooltip: {
            formatter: function (this: any) {
                const point = this.point;
                return `
          <b>Token Holder</b>: ${point.addressName || point.addressHash || 'N/A'}<br/>
          <b>Percentage</b>: ${point.y}%<br/>
          <b>Quantity</b>: ${point.newValue || 'N/A'}<br/>
          <b>Value</b>: ${point.fiatValue || 'N/A'}<br/>
        `;
            }
        },
        plotOptions: {
            pie: {
                innerSize: '60%',
                dataLabels: {
                    enabled: false
                },
                showInLegend: false,
            }
        }
    });

    const fetchTokenData = async () => {
        try {
            setLoading(true);
            const response = await tokenService.getToken(token || holder);
            setStats(response);
            setLoading(false)
        } catch (error) {
            console.error("Error fetching token data:", error);
        } finally {
            setLoading(false);
        }
    };

    const transformTransactions = (transactions: AnalysisList[]): AnalysisList[] => {
        return transactions.map((txn) => {
            const nameLabel = txn.address.name ?? txn.address.hash;
            const newName = nameLabel.toString();
            const newPercent = formatPercent(toFormatNumber(txn.value ?? 0, txn.token.decimals ?? 0), txn.token.total_supply ?? 0) === "0.0000" ?
                formatPercent((txn.token.total_supply && txn.value) ? Number(txn.value ?? 0) : 0, txn.token.total_supply ? Number(txn.token.total_supply) : 1, 2) :
                formatPercent(toFormatNumber(txn.value ?? 0, txn.token.decimals ?? 0), txn.token.total_supply ?? 0, 2);
            const newPercentage = newPercent.toString() + '%';
            const newQuality = toXDCNumber(txn?.value, txn?.token?.decimals, 0);
            const newValue = newQuality.toString();
            const val = getFiat(toXDCNumberWithoutFormat(txn?.value, txn?.token?.decimals) * Number(txn?.token?.exchange_rate ?? 0), 18, fiat, true);
            const fiatValue = val.toString();
            return {...txn, newPercentage, newValue, fiatValue, newName, addressHash: txn.address.hash, addressName: txn.address.name};
        });
    };

    const getTokenCount = async () => {
        try {
            setLoading(true);
            setTransactionList([]);
            const response = await tokenService.getTokenHolders(token || address, serializeQuery({...pagination}));
            setTransactionList(response.items);
            const transformedItems = transformTransactions(response.items);
            setNewTransactionList(transformedItems);
            setTotalRecords(response.count);

            const updatedData = transformedItems.map((item: any) => ({
                name: item.newName,
                y: parseFloat(item.newPercentage),
                newValue: item.newValue,
                fiatValue: item.fiatValue,
                addressHash: item.addressHash,
                addressName: item.addressName
            }));
            //@ts-ignore
            setOptions((prevOptions) => ({
                ...prevOptions,
                series: [{name: 'Tokens', data: updatedData}]
            }));

            setLoading(false);
        } catch (error) {
            console.error("Error fetching token holders data:", error);
            setLoading(false);
        }
    };

    const getTransferCount = async () => {
        try {
            setLoading(true);
            const response = await tokenService.getTransferCount(token || holder);
            setTransferCount(response);
            setLoading(false);
        } catch (err) {
            console.log(err);
            setLoading(false);
        }
    };

    const calculateTopHoldersPercentage = (): number => {
        const totalPercentage = newTransactionList.slice(0, 100).reduce((acc, item) => acc + parseFloat(item.newPercentage || '0'), 0);
        return parseFloat(totalPercentage.toFixed(2));
    };

    const calculateTopTokenValue = (): number => {
        const totalValue = newTransactionList.slice(0, 100).reduce((acc, item) => {
            const value = item.newValue ? item.newValue.replace(/,/g, '') : '0';
            return acc + parseFloat(value);
        }, 0);
        return parseFloat(totalValue.toFixed(2));
    };

    useEffect(() => {
        getTokenCount();
        getTransferCount();
        fetchTokenData();
    }, [pagination, token, holder]);

    return (
        <div className="row">
            <div className="col-lg-12">
                <div className="bs-card h-100">
                    <div className="bs-card-body p-3">
                        <div className="row">
                            <div className="col-lg-6">
                                <div className="bs-card h-100">
                                    <div className="bs-card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between pb-3">
                                            <h2 className="bs-card-header-title">Token Distribution</h2>
                                            <div>
                                                <Dropdown className="d-flex bs_token-social" align="end">
                                                    <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                                                        {selectedOption}
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu variant="dark" align="start" className="custom-menu">
                                                        {['Top 10', 'Top 20', 'Top 50', 'Top 100'].map(option => (
                                                            <Dropdown.Item
                                                                key={option}
                                                                className="bs-small-font"
                                                                onClick={() => handleSelect(option)}
                                                            >
                                                                {option}
                                                            </Dropdown.Item>
                                                        ))}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between pb-3">
                                            <span>
                                                <span className="text-muted">
                                                    Current supply:
                                                </span> &nbsp;
                                                <span>
                                                    {formatUnit(toXDCFormatNumber(stats?.total_supply ?? "0", stats?.decimals), " ")}
                                                </span>
                                            </span>
                                            <span>
                                                <span className="text-muted">
                                                    Total Token Holders:
                                                </span> &nbsp;
                                                <span>
                                                    {formatNumber(transferCount?.token_holders_count ?? "")}
                                                </span>
                                            </span>
                                        </div>
                                        <div className='mb-3'>
                                            <HighchartsReact highcharts={Highcharts} options={options}/>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between pb-3">
                                            <span>
                                                <span className="text-muted me-2">
                                                    The {selectedOption} holders collectively own
                                                </span>
                                                <span>
                                                    {formatNumberWithCommas(calculateTopTokenValue().toString())} Tokens ({calculateTopHoldersPercentage()}%)
                                                </span>
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between pb-3">
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="bs-card h-100">
                                    <div className="bs-card-body p-3">
                                        <TokenPriceChart stats={stats} showPrice={true}/>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6 mt-3">
                                <div className="bs-card h-100">
                                    <div className="bs-card-body p-3">
                                        <TokenPriceChart stats={stats} showMarketCap={true}/>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6 mt-3">
                                <div className="bs-card h-100">
                                    <div className="bs-card-body p-3">
                                        <TokenPriceChart stats={stats} showTotalVolume={true}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalysisTab;